import { Button } from '@app/components/various/Button';
import { Card } from '@app/components/various/Card';
import { Download } from '@app/components/various/Icons';
import { Status } from '@app/components/various/Status';
import { getCalculatedInvoiceStatus, getIsInvoiceStandard } from '@app/logic/invoices';
import { compiledPaths } from '@app/paths';
import { useInvoiceDownload } from '@app/services/hooks/invoice/useInvoiceDownload';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { isDefined } from '@app/utils/is';
import { getInvoiceStatusColor } from '@app/utils/status';
import { Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Invoice } from '../../../../../typings';
import { InvoiceStatusLabel } from '../InvoiceStatusLabel';

import { BaseInvoiceCard } from './BaseInvoiceCard';

interface Props {
  row: Row<Invoice.AnyInvoice>;
}

export const InvoicesListCard = ({ row }: Props) => {
  const { t } = useTranslation(['common', 'invoices', 'orders', 'totals']);
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'invoicesTable' });
  const { downloadInvoice } = useInvoiceDownload();

  if (!getIsInvoiceStandard(row.original)) {
    return <BaseInvoiceCard invoice={row.original} columnsVisibility={columnsVisibility} />;
  }

  const {
    createdAt,
    dueDate,
    invoiceNumber,
    orderNumber,
    address,
    grandTotalWithoutTax,
    grandTotal,
    totalDue,
    invoiceHash,
    invoice,
    order,
  } = row.original;

  const handleDownloadClick = () => {
    if (isDefined(invoiceHash) && isDefined(invoice) && isDefined(invoiceNumber)) {
      downloadInvoice({ invoiceHash, invoiceId: invoice, invoiceNumber });
    }
  };

  const status = getCalculatedInvoiceStatus(row.original);
  const shouldShowPayButton = row.original.isPayable;
  const orderPath = isDefined(order) ? compiledPaths.CHECKOUT_SUCCESS({ id: order }) : '';

  return (
    <Card.Wrapper markerColor={getInvoiceStatusColor(status)}>
      {columnsVisibility.status?.isVisible && (
        <Card.Header>
          <Status type={status} />
          <span>
            {t('common:status')}:{' '}
            <span className="text-neutral-20">
              <InvoiceStatusLabel status={status} />
            </span>
          </span>
        </Card.Header>
      )}
      <Card.Title>#{invoiceNumber}</Card.Title>
      <Card.AttributeList>
        <Card.AttributeLink to={orderPath} name={t('orders:order_no')} value={orderNumber ?? undefined} />
        {columnsVisibility.address?.isVisible && <Card.Attribute name={t('invoices:invoice_address')} value={address}></Card.Attribute>}
        {columnsVisibility.createdAt?.isVisible && <Card.Attribute name={t('common:created')} value={createdAt}></Card.Attribute>}
        {columnsVisibility.dueDate?.isVisible && <Card.Attribute name={t('common:due_date')} value={dueDate}></Card.Attribute>}
      </Card.AttributeList>
      <Card.AttributeList orientation="horizontal">
        {columnsVisibility.grandTotalWithoutTax?.isVisible && (
          <Card.Attribute name={t('totals:total_no_tax')} value={grandTotalWithoutTax} orientation="vertical" />
        )}
        {columnsVisibility.grandTotal?.isVisible && (
          <Card.Attribute name={t('totals:total_with_tax')} value={grandTotal} orientation="vertical" />
        )}
        {columnsVisibility.totalDue?.isVisible && (
          <Card.Attribute name={t('totals:total_due_with_tax')} value={totalDue} orientation="vertical" />
        )}
      </Card.AttributeList>
      {shouldShowPayButton && (
        <div className="flex justify-between gap-2 pt-2">
          <Button size="medium" as={Link} to={compiledPaths.INVOICE_DETAILS({ invoiceHash, invoiceId: invoice })}>
            {t('invoices:pay_invoice')}
          </Button>
        </div>
      )}
      <Button
        size="medium"
        variant="bordered"
        color="secondary"
        aria-label={t('invoices:download_invoice')}
        className="absolute right-4 top-4 z-interactiveTableItem"
        icon={Download}
        onClick={handleDownloadClick}
      />
    </Card.Wrapper>
  );
};
